import { Component, OnInit, AfterViewInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { distinctUntilChanged, Observable, Subscription, switchMap, tap, finalize, catchError, throwError, of, map} from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TableColumnModel } from '../../../Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../Forms/enums/table-column-input-type.enum';
import { Mention } from '../../models/mention';
import { TagModel } from '../../../Tags/models/tag.model';
import { TagsService } from '../../../Tags/services/tags.service';
import { ChipModel } from '../../../Forms/models/chip.model';
import { AutocompleteCustomOption, AppearCondition } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { DateHelper } from '../../../Forms/helpers/date.helper';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MessageService } from 'primeng/api';
import { TextInputDialogComponent } from '../../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { AutocompleteComponent } from '../../../Forms/components/Autocomplete/autocomplete.component';
import { MentionContentCustomizationEnum } from '../../enums/mention-content-customization.enum';
import { mentionBadgesSaved } from '../MentionCard/mention-card.component';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { NewResourceDialogComponent } from '../../../Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Layout/models/display-group.model';
import { PartialMentionApiModel, MentionModel } from '../../../../api/mentions/model/mention.model';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { MentionSearchService } from '../../../../Monitor/Core/services/mention-search.service';
import { MonitoringRoutingService } from '../../../../Monitor/Core/services/monitoring-routing.service';
import { MonitoringRouteModel } from '../../../../Monitor/Shared/models/monitoring-route.model';
import { RouteType } from '@Monitor/Shared/enums/route-type.enum';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { MediaSubscriptionService } from '../../../../Monitor/Core/services/media-subscription.service';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';
import { TableComponent } from '../../../Forms/components/Table/table.component';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';

enum ManageMentionOrganizationEnum {
    tag = 'tag',
    folder = 'folder'
}

export interface TableRow<T> {
    id: number;
    name: string;
    editDisabled: boolean;
    selected: boolean;
    rawData: T;
    tooltip: string;
    tooltipInfo?: String;
}

@Component({
    selector: 'agpr-manage-mention-organization-dialog',
    templateUrl: './manage-mention-organization-dialog.component.html',
    styleUrls: ['./manage-mention-organization-dialog.component.scss'],
    standalone: false
})

export class ManageMentionOrganizationDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    readonly id: string = 'agpr_manageMentionOrganizationDialog';
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly mentionContentCustomizationEnum = MentionContentCustomizationEnum;
    readonly mentionBadgesSaved = mentionBadgesSaved;
    readonly manageMentionOrganizationEnum = ManageMentionOrganizationEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() set mention(value: Mention) {
        this.mentionsService.setFocusedMention(value);
        this.initMention(value);
    }
    @Input() inputFolderIds: number[];
    @Input() inputTagIds: number[];

    @ViewChild('tagAutocomplete') tagAutocomplete: AutocompleteComponent;
    @ViewChild('folderAutocomplete') folderAutocomplete: AutocompleteComponent;
    @ViewChild('tagsTable') tagsTable: TableComponent;
    @ViewChild('foldersTable') foldersTable: TableComponent;

    private subscriptions: Subscription[] = [];
    hideSlideOverlay: boolean = false;
    mentionOrganizationUpdated: boolean = false;
    selectedTabIndex: number = 0;
    columnDefs: TableColumnModel[] = [];
    nameColumn: TableColumnModel = {
        type: TableColumnInputEnum.text,
        heading: 'Mention.manageMentionOrganization.table.name',
        field: 'name',
        showInfoCircle: true,
        sortable: true,
        sortType: 'text',
        selectable: true,
        selectField: 'selected'
    };

    loading: boolean = false;
    currentRoute: MonitoringRouteModel;
    search: MentionSearchModel;
    subscription: MediaSubscription;
    _mention: Mention;
    tagInitChips: ChipModel[] = [];
    folderInitChips: ChipModel[] = [];

    tagRows: TableRow<TagModel>[] = [];
    tagFilteredRows: TableRow<TagModel>[] = [];
    tagIds: Set<number> = new Set();
    tagChips: ChipModel[] = [];
    addedTags: TableRow<TagModel>[] = [];
    removedTags: TableRow<TagModel>[] = [];

    folderRows: TableRow<Folder>[] = [];
    folderFilteredRows: TableRow<Folder>[] = [];
    folderIds: Set<number> = new Set();
    folderChips: ChipModel[] = [];
    addedFolders: TableRow<Folder>[] = [];
    removedFolders: TableRow<Folder>[] = [];

    tagsAutocompleteOptions:  AutocompleteCustomOption[] = [
        {
            label: 'Mention.manageMentionOrganization.createTag',
            action: (filterString: string) => {
                this.createTag(filterString).subscribe();
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    foldersAutocompleteOptions:  AutocompleteCustomOption[] = [
        {
            label: 'Mention.manageMentionOrganization.createFolder',
            action: (filterString: string) => {
                this.createFolder(filterString).subscribe();
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    constructor(
        public monitoringRoutingService: MonitoringRoutingService,
        public coreDialogService: CoreDialogService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public usersService: UsersService,
        public tagsService: TagsService,
        public folderService: FolderService,
        public topicsService: TopicsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public mentionsService: MentionsService,
        public mentionSearchService: MentionSearchService
    ) {
        super(coreDialogService, 'ManageMentionOrganization');
    }

    ngOnInit(): void {
        if (this.inputTagIds) {
            this.tagIds = new Set(this.inputTagIds);
            this.initializeChips(ManageMentionOrganizationEnum.tag);
        }
        if (this.inputFolderIds) {
            this.folderIds = new Set(this.inputFolderIds);
            this.initializeChips(ManageMentionOrganizationEnum.folder);
        }
        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().subscribe(route => {
                this.currentRoute = route;
            }),
            this.topicsService.getFocusedTopic().subscribe(search => {
                this.search = search;
            }),
            this.mediaSubscriptionService.currentResource$.subscribe(subscription => {
                this.subscription = subscription;
            }),
            this.mentionsService.getFocusedMention().pipe(distinctUntilChanged()).subscribe(mention => {
                this.initMention(mention);
            }),
            this.tagsService.all$.subscribe(tags => this.initTableRows(ManageMentionOrganizationEnum.tag, tags)),
            this.folderService.all$.subscribe(folders => this.initTableRows(ManageMentionOrganizationEnum.folder, folders))
        );
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.columnDefs = [this.nameColumn];
        });
    }

    initMention(mention: Mention): void {
        if (!mention) {
            return;
        }
        this._mention = mention;
        this.tagIds = new Set(this.inputTagIds || this._mention.tagIds);
        this.initializeChips(ManageMentionOrganizationEnum.tag);
        this.folderIds = new Set(this.inputFolderIds || this._mention.folderIds);
        this.initializeChips(ManageMentionOrganizationEnum.folder);
    }

    initTableRows<T extends TagModel | Folder>(type: ManageMentionOrganizationEnum, items?: T[]) {
        const modifiedKey: string = type === ManageMentionOrganizationEnum.tag
            ? 'lastModified'
            : type === ManageMentionOrganizationEnum.folder
                ? 'modified'
                : null;
        if (!items) {
            return;
        }
        const userIds: Set<number> = new Set();
        const availableItems: TableRow<T>[] = [];
        items.forEach(item => {
            userIds.add(item.createdByUserId);
            userIds.add(item[modifiedKey + 'ByUserId']);

            availableItems.push(<TableRow<T>>{
                id: item.id,
                name: item.name,
                tooltip: item.name,
                selected: this[type + 'Ids'].has(item.id),
                editDisabled: true,
                rawData: item,
            });
        });
        this[type + 'Rows'] = availableItems;
        this[type + 'FilteredRows'] = availableItems;
        this.initTooltipInfo(type, [...userIds]);
        this.initializeChips(type);
    }

    initTooltipInfo(type: ManageMentionOrganizationEnum, userIds: number[]) {
        const modifiedKey: string = type === ManageMentionOrganizationEnum.tag
            ? 'lastModified'
            : type === ManageMentionOrganizationEnum.folder
                ? 'modified'
                : null;
        this.usersService.getUsers(userIds).subscribe(users => {
            this[type + 'Rows'].forEach(row => {
                let tooltipInfo = '';
                const creator = users.find(i => i.id === row.rawData.createdByUserId);
                if (creator) {
                    const createdBy = creator.getDisplayName();
                    const createdDate = DateHelper.toDateString(new Date(row.rawData.createdDate));
                    tooltipInfo += this.localizationService.transform('Mention.manageMentionOrganization.infoIcon.createdBy', { createdBy })
                        + this.localizationService.transform('Mention.manageMentionOrganization.infoIcon.createdDate', { createdDate });
                }
                const modifier = users.find(i => i.id === row.rawData[modifiedKey + 'ByUserId']);
                if (modifier) {
                    const modifiedBy = modifier.getDisplayName();
                    const modifiedDate = DateHelper.toDateString(new Date(row.rawData[modifiedKey + 'Date']));
                    tooltipInfo += this.localizationService.transform('Mention.manageMentionOrganization.infoIcon.modifiedBy', { modifiedBy })
                        + this.localizationService.transform('Mention.manageMentionOrganization.infoIcon.modifiedDate', { modifiedDate });
                }
                row.tooltipInfo = tooltipInfo;
            });
        });
    }

    initializeChips(type: ManageMentionOrganizationEnum) {
        this[type + 'Chips'] = [];
        this[type + 'Rows'].forEach(row => {
            row.selected = this[type + 'Ids'].has(row.id);
            if(row.selected) {
                this[type + 'Chips'].push(row.rawData.toChipModel());
            }
        });
        this[type + 'InitChips'] = this[type + 'Chips'].slice();
        this.updateInfo();
    }

    resetTableRows(type: ManageMentionOrganizationEnum) {
        this[type + 'FilteredRows'] = this[type + 'Rows'];
    }

    filterTableRows(type: ManageMentionOrganizationEnum, filterString: string) {
        if (filterString.length === 0) {
            this.resetTableRows(type);
        }
        this[type + 'FilteredRows'] = this[type + 'Rows']
            .filter(row => row.name.toLowerCase().indexOf(filterString.toLowerCase()) > -1);
    }

    selectionChanged(type: ManageMentionOrganizationEnum, row: TableRow<TagModel | Folder>) {
        if(row.selected){
            this[type + 'Ids'].add(row.id);
            this[type + 'Chips'].push(row.rawData.toChipModel());
        } else {
            this[type + 'Ids'].delete(row.id);
            const idx = this[type + 'Chips'].findIndex(chip => chip.id === row.id);
            if(idx > -1) {
                this[type + 'Chips'].splice(idx, 1);
            }
        }
        this.updateInfo();
    }

    allSelectionChanged(type: ManageMentionOrganizationEnum, allSelected: boolean) {
        if(allSelected){
            this[type + 'Ids'].clear();
            this[type + 'Chips'] = [];
            this[type + 'Rows'].forEach(({id, rawData}) => {
                this[type + 'Ids'].add(id);
                this[type + 'Chips'].push(rawData.toChipModel());
            });
        } else {
            this[type + 'Ids'].clear();
            this[type + 'Chips'] = [];
        }
        this.updateInfo();
    }

    removeChip(type: ManageMentionOrganizationEnum, idx: number) {
        const { id } = this[type + 'Chips'][idx];
        this[type + 'Chips'].splice(idx, 1);
        this[type + 'Ids'].delete(id);
        const item = this[type + 'Rows'].find(row => row.id === id);
        if(item) {
            item.selected = false;
        }
        this.updateInfo();
    }

    removeAllChips(type: ManageMentionOrganizationEnum) {
        this[type + 'Rows'].forEach(row => {
            row.selected = false;
        });

        this[type + 'Ids'].clear();
        this[type + 'Chips'] = [];
        this.updateInfo();
    }

    updateInfo() {
        this.removedTags = this._mention.tagIds
            .filter(id => !this.tagIds.has(id))
            .map(id => this.tagRows.find(tag => tag.id === id))
            .filter(tag => !! tag);
        this.addedTags = [...this.tagIds]
            .filter(id => !this._mention.tagIds.includes(id))
            .map(id => this.tagRows.find(tag => tag.id === id))
            .filter(tag => !! tag);
        this.removedFolders = this._mention.folderIds
            .filter(id => !this.folderIds.has(id))
            .map(id => this.folderRows.find(tag => tag.id === id))
            .filter(folder => !! folder);
        this.addedFolders = [...this.folderIds]
            .filter(id => !this._mention.folderIds.includes(id))
            .map(id => this.folderRows.find(folder => folder.id === id))
            .filter(folder => !! folder);

        if(this.tagsTable && this.tagIds.size === 0) {
            this.tagsTable.selectAll = false;
        }
        if(this.foldersTable && this.folderIds.size === 0) {
            this.foldersTable.selectAll = false;
        }
    }

    createTag(name: string) {
        const tagModel = new TagModel();
        tagModel.name = name;
        return this.tagsService.createResource(tagModel).pipe(
            tap(id => {
                this.tagIds.add(id);
                this.tagAutocomplete.clearInput();
            }),
            switchMap(() => this.tagsService.refreshAllManual())
        );
    }

    createFolder(name: string, group?: DisplayGroupModel) {
        const folderModel = new Folder();
        folderModel.name = name;
        if (group) {
            folderModel.groupId = group.id;
        }
        return this.folderService.create(folderModel, group).pipe(
            tap(id => {
                this.folderIds.add(id);
                this.folderAutocomplete.clearInput();
            }),
            switchMap(() => this.folderService.refreshAllManual())
        );
    }

    onpenCreateTagDialog() {
        if (this.tagAutocomplete?.autocomplete) {
            this.tagAutocomplete.autocomplete.hide();
        }
        this.coreDialogService.open(
            TextInputDialogComponent,
            {
                inputs: {
                    width: 300,
                    heading: 'Tags.manageDialog.newTag.heading',
                    description: 'Tags.manageDialog.newTag.description',
                    placeholder: 'Tags.manageDialog.newTag.placeholder',
                    okLabel: 'Tags.manageDialog.newTag.okLabel',
                    disabledButtonTooltip:  'Tags.manageDialog.newTagGroup.disabledTooltip',
                    formValidator: (name: string) =>
                        this.tagRows.find(tagRow => tagRow.name.toLocaleLowerCase() === name.toLocaleLowerCase())
                            ? ['Tags.manageDialog.newTag.error.duplicateName']
                            : [],
                    submit: (name: string) => this.createTag(name)
                }
            }
        );
    }

    onpenCreateFolderDialog() {
        if (this.folderAutocomplete?.autocomplete) {
            this.folderAutocomplete.autocomplete.hide();
        }
        this.coreDialogService.open(
            NewResourceDialogComponent,
            {
                inputs: {
                    width: 300,
                    heading: 'Monitoring.result.newFolderDialogTitle',
                    description: 'Monitoring.newFolderMessage',
                    placeholder: 'Monitoring.enterFolderNameHint',
                    okLabel: 'Monitoring.createFolder',
                    disabledButtonTooltip: 'Monitoring.result.newFolder.disabledTooltip',
                    formValidator: (name: string) =>
                        this.tagRows.find(tagRow => tagRow.name.toLocaleLowerCase() === name.toLocaleLowerCase())
                            ? ['Monitoring.result.newFolder.error.duplicate']
                            : [],
                    submit: (name: string, group?: DisplayGroupModel) => this.createFolder(name, group)
                }
            }
        );
    }

    updateMention() {
        this.loading = true;
        const tagIds = this.removedTags.length > 0 || this.addedTags.length > 0 ? [...this.tagIds] : null;
        const folderIds = this.removedFolders.length > 0 || this.addedFolders.length > 0 ? [...this.folderIds] : null;

        let request;

        if(this._mention.mentionId) {
            request = this.patchMention(tagIds, folderIds);
        } else {
            request = this.saveMention();
        }

        request.pipe(
            tap(() => {
                // When the mention is removed from the current folder, refresh the search
                if (
                    this.currentRoute.type === RouteType.folder &&
                    folderIds &&
                    !folderIds.includes(+this.currentRoute.value)
                ) {
                    this.mentionSearchService.search(true, true);
                }
            }),
            switchMap(() => {
                const req: Observable<MentionModel[]> = this._mention.mentionId
                    ? this.mentionsService.getMentionByIds([this._mention.mentionId]).pipe(
                        map(res => [res[this._mention.mentionId]])
                    )
                    : this.mentionsService.getMentionByMercuryIds([this._mention.id]);
                return req.pipe(
                    tap(mentions => {
                        if (!mentions) {
                            return of(null);
                        }
                        this._mention = new Mention(mentions[0]);
                        this.mentionOrganizationUpdated = true;
                    }),
                    catchError(err => {
                        console.error('Failed to get mention by Mercury ID', err);
                        return of(null);
                    })
                );
            }),
            finalize(() => {
                this.loading = false;
                this.hideSlideOverlay = true;
            })
        ).subscribe();
    }

    saveMention(): Observable<any> {
        const mention = [this._mention];
        const search = this.subscription ? this.subscription.toMentionSearchModel() : this.search;
        const tags = this.addedTags.map(tagRow => tagRow.rawData);
        const folders = this.addedFolders.map(folderRow => folderRow.rawData);
        const hasNoTags = tags.length === 0;

        return folders.length > 0
            ? this.folderService.saveMentionsToFolder(mention, folders, hasNoTags, search, tags)
            : this.mentionsService.saveMentionsToAccount(mention, hasNoTags, search, tags);
    }

    patchMention(tagIds?: number[], folderIds?: number[]): Observable<any> {
        const patch: PartialMentionApiModel = { id: this._mention.mentionId };

        if(tagIds) {
            patch.tagIds = tagIds;
        }

        if(folderIds){
            patch.folderIds = folderIds;
        }

        return this.mentionsService.patchAndReturnMention(patch).pipe(
            tap(() => {

                if(folderIds?.length) {
                    this.folderService.refreshAllManual().subscribe();
                }

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Mention.manageMentionOrganization.updateMention.successMessage.summary',
                    detail: 'Mention.manageMentionOrganization.updateMention.successMessage.detail'
                });
            }),
            catchError(error => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Mention.manageMentionOrganization.updateMention.failureMessage.summary',
                    detail: 'Mention.manageMentionOrganization.updateMention.failureMessage.detail'
                });
                return throwError(() => error);
            })
        );

    }

    removeFromAccount() {
        const io = {
            inputs: {
                heading: 'Mention.manageMentionOrganization.button.removeFromAccount',
                message: this.localizationService.transform(
                    'Mention.manageMentionOrganization.button.removeFromAccount.message',
                    {folderCount: this._mention.folderIds.length, tagCount: this._mention.tagIds.length}
                ),
                okLabel: 'Core.button.remove'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.mentionsService.removeMentionsFromAccount([this._mention], null, 1).pipe(
                            tap(() => this.mentionSearchService.search(true, true)),
                            switchMap(() => this.folderService.refreshAllManual()),
                            finalize(() => this.hideSlideOverlay = true)
                        ).subscribe();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    cancel() {
        this.hideSlideOverlay = true;
    }

    handleVisibleChange() {
        if (this.mentionOrganizationUpdated) {
            this.closeDialog(this._mention);
            return;
        }
        this.closeDialog();
    }
}
